import { useEffect, useState } from 'react';

/**
 * The mobile <-> desktop handoff.
 *
 * 1024px is `--dsk-bp-desktop` in the design system and the line
 * DESKTOP-PREFERENCES.md draws: below it the mobile `ds-*` track owns the view,
 * at and above it the desktop `dsk-*` track does.
 *
 * The app previously had THREE competing JS thresholds (480, 700, 1024) and a
 * dominant CSS breakpoint of 768 that matched none of them. These constants are
 * now the only ones. `DS_BP` is exported so CSS-side media queries and any
 * remaining one-off checks can be kept honest against the same numbers.
 *
 * Use this hook ONLY when the DOM genuinely differs between viewports (shells,
 * bottom sheet vs modal, filter sheet vs filter rail, back button vs
 * breadcrumbs). When only presentation changes, use a CSS media query instead:
 * it costs no JS, has no hydration flash, and cannot desync.
 */
export const DS_BP = {
  /** Small phone refinements inside the mobile track. */
  sm: 640,
  /** The mobile track's upper bound. */
  md: 768,
  /** The mobile <-> desktop handoff. Matches --dsk-bp-desktop. */
  desktop: 1024,
};

const QUERY = `(min-width: ${DS_BP.desktop}px)`;

/** Read synchronously so the first paint is already correct, no layout flash. */
function readIsDesktop() {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia(QUERY).matches;
}

/**
 * @returns {{ isDesktop: boolean, isMobile: boolean, track: 'desktop'|'mobile' }}
 */
export function useViewport() {
  const [isDesktop, setIsDesktop] = useState(readIsDesktop);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mql = window.matchMedia(QUERY);
    const onChange = (event) => setIsDesktop(event.matches);

    // Re-read on mount: the media state can have changed between the initial
    // render and the effect firing.
    setIsDesktop(mql.matches);

    // Safari below 14 only has the deprecated addListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return {
    isDesktop,
    isMobile: !isDesktop,
    track: isDesktop ? 'desktop' : 'mobile',
  };
}

export default useViewport;
